import { forwardRef, Inject, Injectable } from "@angular/core";
import { IAuthService, CHANNEL_AUTH_USER_SERVICE } from "../IAuthService";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { LogonResp } from "../../model/core/LogonResp";
import { Subscription, Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import { SessionStoreData } from "../../model/core/SessionStoreData";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ActiveUser } from "shared/cubiq-services/model/core/ActiveUser";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { OrderDataPromiseLookupService } from "shared/cubiq-services/lookup/model/impl/OrderDataPromiseLookupService";
import { ScratchPadDataPromiseLookupService } from "shared/cubiq-services/lookup/model/impl/ScratchPadDataPromiseLookupService";
import { TranslateService } from "@ngx-translate/core";
import { TraderGroupLookupService } from "../../lookup/model/impl/TraderGroupLookupService";
import { UserAuthPromiseService } from "./UserAuthPromiseService";

@Injectable()
export class UserAuthPrimaryService extends UserAuthPromiseService implements IAuthService {
  private broker: ServiceMessageBroker;
  protected ports: Array<MessagePort> = new Array<MessagePort>();
  private workerTokenSubscription: Subscription;
  private sendLastToken: boolean = false;
  private currentStoreData: SessionStoreData = null;
  private _tokenEventSubject: Subject<SessionStoreData> = new Subject<SessionStoreData>();
  private activeUser: ActiveUser;
  private hedgeOrderMonitorService: { getStagingOrderCount(): number };

  constructor(
    private cW: CubiqWorker,
    private factory: ServiceMessageBrokerFactory,
    private activeUserService: ActiveUserProfilePromiseService,
    private dataService: OrderDataPromiseLookupService,
    private translationService: TranslateService,
    private traderGroupLookup: TraderGroupLookupService,
    private scratchPadDataService: ScratchPadDataPromiseLookupService,
  ) {
    super();
    this.init();
  }

  private init(): void {
    this.broker = this.factory.createMessageBroker(CHANNEL_AUTH_USER_SERVICE, true, true);
    this.broker.registerMethod("isResetRequested", [], this.isResetRequested.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "disconnectOnLogout",
      [],
      this.disconnectOnLogout.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "requestLogin",
      [PRIMITIVE, PRIMITIVE],
      this.requestLogin.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "initTokenPort",
      [PRIMITIVE],
      this.initTokenPort.bind(this),
      null,
      false,
    );
    this.broker.registerMethod(
      "requestAppRefresh",
      [PRIMITIVE],
      this.requestAppRefresh.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getLogoutWarnings",
      [PRIMITIVE, PRIMITIVE],
      this.getLogoutWarnings.bind(this),
      PRIMITIVE,
    );

    this.workerTokenSubscription = this.cW.tokenEvent$.subscribe((event: SessionStoreData) => {
      this.currentStoreData = event;
      this._tokenEventSubject.next(event);
      if (this.ports && this.ports.length > 0) {
        this.sendMessageToAll(event);
      } else {
        this.sendLastToken = true;
      }
    });
    this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
      this.activeUser = data;
    });
  }

  isResetRequested(): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      res(this.cW.getIsResetRequested());
    });
  }

  disconnectOnLogout(): Promise<any> {
    return new Promise<void>((res, rej) => {
      res(this.cW.disconectOnLogout());
    });
  }

  requestLogin(user: any, timeout: number, sso: boolean): Promise<LogonResp> {
    return new Promise<LogonResp>((res, rej) => {
      res(this.cW.requestLogin(user, timeout, sso));
    });
  }

  requestAppRefresh(storeData: SessionStoreData): Promise<any> {
    return new Promise<void>((res, rej) => {
      res(this.cW.refreshAppRequest(storeData));
    });
  }

  public getTokenEventSubject(): Subject<SessionStoreData> {
    return this._tokenEventSubject;
  }

  public initTokenPort(port: MessagePort): void {
    this.ports.push(port);
    if (this.sendLastToken && this.currentStoreData != null) {
      this.sendMessageToAll(this.currentStoreData);
    }
  }

  private sendMessageToAll(message: any): void {
    this.ports.forEach((port) => {
      port.postMessage(message);
    });
  }

  public getLogoutWarnings(isTrader: boolean, hasOrdersInDi: boolean): Promise<Array<string>> {
    return new Promise<Array<string>>((res, rej) => {
      let warningMessages: Set<string> = new Set<string>();
      let hasOrdersInTraderGroup: boolean = !isTrader;
      let hasOrdersInAcceptance: boolean = false;
      let hasOrdersInAcceptanceExternal: boolean = false;
      let hasOrdersInUnsavedState: boolean = false;

      this.dataService.getAll().then((orders) => {
        let ordersCount: number = 0;

        while (
          ordersCount < orders.length &&
          (!hasOrdersInAcceptance || !hasOrdersInTraderGroup || !hasOrdersInAcceptanceExternal)
        ) {
          let ord: CubiqTS.OrderDataImpl = orders[ordersCount];
          if (
            !hasOrdersInAcceptance &&
            (ord.orderState === CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED ||
              ord.orderState === CubiqTS.OrderState.ACCEPTANCE_NEEDED ||
              ord.orderState === CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED ||
              ord.targetDeskId)
          ) {
            if (this.shouldTriggerExternalWarning(ord)) {
              warningMessages.add(
                this.translationService.instant("logout.warning.orders.in.td.external"),
              );
              hasOrdersInAcceptanceExternal = true;
            } else {
              if (this.shouldTriggerAcceptanceWarning(ord)) {
                warningMessages.add(this.translationService.instant("logout.warning.acceptance"));
                hasOrdersInAcceptance = true;
              }
            }
          }
          if (!hasOrdersInTraderGroup && this.shoudlTriggerOwnTdWarning(ord)) {
            hasOrdersInTraderGroup = true;
            warningMessages.add(this.translationService.instant("logout.warning.orders.in.td"));
          }
          ordersCount++;
        }

        if (hasOrdersInDi) {
          warningMessages.add(this.translationService.instant("logout.warning.di"));
        }

        if (
          this.hedgeOrderMonitorService != null &&
          this.hedgeOrderMonitorService.getStagingOrderCount() > 0
        ) {
          warningMessages.add(
            this.translationService.instant("hedge.order.monitor.staging.warning.logout"),
          );
        }

        this.scratchPadDataService.getAll().then((orders) => {
          let scratchPadOrdersCount = 0;
          if (orders && Array.isArray(orders) && orders.length > 0) {
            while (scratchPadOrdersCount < orders.length && !hasOrdersInUnsavedState) {
              let ord: any = orders[scratchPadOrdersCount];
              if (
                (ord.rootOrder && !ord.rootOrder.hasOwnProperty("orderState")) ||
                (ord.fixingOrderData && !ord.fixingOrderData.hasOwnProperty("orderState"))
              ) {
                warningMessages.add(
                  this.translationService.instant("logout.warning.orders.unsaved"),
                );
                hasOrdersInUnsavedState = true;
              }
              scratchPadOrdersCount++;
            }
          }
          res(Array.from(warningMessages.values()));
        });
      });
    });
  }

  private shouldTriggerAcceptanceWarning(ord: CubiqTS.OrderDataImpl) {
    if (
      ord.lockUserId != null &&
      ((ord.lockType && ord.lockType === CubiqTS.LockType.MODIFY_LOCK) ||
        ord.orderState === CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED ||
        ord.orderState === CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED)
    )
      return ord.lockUserId.toString() === this.activeUser.userId;
    else if (ord.orderState === CubiqTS.OrderState.ACTIVE && ord.targetDeskId)
      return ord.transferUserId.toString() === this.activeUser.userId;
    return ord.entryUserId && ord.entryUserId.toString() === this.activeUser.userId;
  }

  private shouldTriggerExternalWarning(ord: CubiqTS.OrderDataImpl) {
    return (
      ord.orderState === CubiqTS.OrderState.ACCEPTANCE_NEEDED &&
      this.traderGroupLookup.getTraderGroup(ord.deskId).isExternallyFilledFg
    );
  }

  private shoudlTriggerOwnTdWarning(ord: CubiqTS.OrderDataImpl) {
    return this.activeUser.isGroupMember(ord.deskId.toString());
  }

  private shouldTriggerSavedWarning(ord: CubiqTS.OrderDataImpl) {}

  public registerHOM(hom: { getStagingOrderCount(): number }) {
    this.hedgeOrderMonitorService = hom;
  }
}
